/**
 * PremiumReportWorker - Worker för slutrapport-generering med premium-modell
 * 
 * Ansvarig för:
 * - Processning av aggregerade resultat
 * - Anrop till callPremiumText() med GPT-5-premium (fallback till GPT-4.1-premium)
 * - Generering av professionella rapporter på svenska
 * - Sammanslagning av modulsektioner till slutrapport
 */

import { supabase } from '@/integrations/supabase/client';
import { callPremiumText } from '@/lib/azure-openai';
import { AggregateResult } from './types';

export class PremiumReportWorker {
  private workerId: string;

  constructor(workerId: string) {
    this.workerId = workerId;
  }

  /**
   * Genererar slutrapport baserat på aggregerade resultat
   */
  async generateFinalReport(revisionId: string): Promise<string> {
    try {
      console.log(`Genererar slutrapport för revision ${revisionId}`);

      // 1. Hämta aggregerade resultat för alla moduler
      const aggregates = await this.getAggregatedResults(revisionId);
      
      if (aggregates.length === 0) {
        throw new Error('Inga aggregerade resultat hittades');
      }

      // 2. Hämta revision-info
      const revisionInfo = await this.getRevisionInfo(revisionId);

      // 3. Generera executive summary
      const executiveSummary = await this.generateExecutiveSummary(revisionInfo, aggregates);

      // 4. Generera sektioner per modul
      const moduleSections = await this.generateModuleSections(aggregates);

      // 5. Generera rekommendationer
      const recommendations = await this.generateRecommendations(aggregates);

      // 6. Sammanställ slutrapport
      const finalReport = this.assembleFinalReport({
        revisionInfo,
        executiveSummary,
        moduleSections,
        recommendations,
        aggregates
      });

      // 7. Spara rapport i databasen
      await this.saveFinalReport(revisionId, finalReport, executiveSummary);

      console.log(`Slutrapport genererad för revision ${revisionId}`);
      return finalReport.report_html;

    } catch (error) {
      console.error(`Fel vid generering av slutrapport för ${revisionId}:`, error);
      throw error;
    }
  }

  // === PRIVATE METHODS ===

  private async getAggregatedResults(revisionId: string): Promise<AggregateResult[]> {
    const { data: aggregates, error } = await supabase
      .from('aggregates')
      .select('*')
      .eq('revision_id', revisionId);

    if (error) throw error;

    return aggregates || [];
  }

  private async getRevisionInfo(revisionId: string): Promise<any> {
    const { data: revision, error } = await supabase
      .from('revisions')
      .select('*')
      .eq('id', revisionId)
      .single();

    if (error) throw error;
    return revision;
  }

  private async generateExecutiveSummary(revisionInfo: any, aggregates: AggregateResult[]): Promise<string> {
    const totalFindings = aggregates.reduce((sum, agg) => sum + agg.flag_count, 0);
    const criticalFindings = aggregates.reduce((sum, agg) => sum + agg.high_risk_flags, 0);
    const totalTransactions = aggregates.reduce((sum, agg) => 
      sum + (agg.summary_json?.total_transactions || 0), 0);

    const prompt = `Du är en erfaren auktoriserad revisor som skriver en executive summary för en revisionsrapport.

UPPDRAGSINFORMATION:
- Företag: ${revisionInfo.company_name}
- Organisationsnummer: ${revisionInfo.org_number}
- Revisionsperiod: ${new Date(revisionInfo.started_at).getFullYear()}
- Totalt antal transaktioner: ${totalTransactions.toLocaleString('sv-SE')}
- Totalt antal fynd: ${totalFindings}
- Kritiska fynd: ${criticalFindings}

MODULER ANALYSERADE:
${aggregates.map(agg => `- ${agg.module}: ${agg.flag_count} fynd, riskpoäng ${agg.risk_score}`).join('\n')}

Skriv en professionell executive summary på svenska som:
1. Sammanfattar revisionens omfattning och syfte
2. Lyfter fram de viktigaste fynden och riskområdena
3. Ger en övergripande bedömning av företagets finansiella kontroller
4. Är skriven i formell revisionsrapport-stil
5. Är ca 200-300 ord

Använd professionell svenska och terminologi som är vanlig inom revision.`;

    const summary = await callPremiumText([
      { role: 'system', content: 'Du är en auktoriserad revisor som skriver professionella rapporter på svenska.' },
      { role: 'user', content: prompt }
    ]);

    // Varna om token-limit nåtts (teoretiskt med 16K gräns)
    console.log(`📝 Executive summary genererad (längd: ${summary.length} tecken)`);
    if (summary.length > 60000) {
      console.warn(`⚠️ Executive summary extremt lång (${summary.length} tecken) - kan ha nått token-limit`);
    }

    return summary;
  }

  private async generateModuleSections(aggregates: AggregateResult[]): Promise<Record<string, string>> {
    const sections: Record<string, string> = {};

    for (const aggregate of aggregates) {
      const section = await this.generateModuleSection(aggregate);
      sections[aggregate.module] = section;
    }

    return sections;
  }

  private async generateModuleSection(aggregate: AggregateResult): Promise<string> {
    const topRisks = aggregate.summary_json?.all_flags
      ?.sort((a: any, b: any) => b.score - a.score)
      ?.slice(0, 5) || [];

    const severityDistribution = {
      critical: topRisks.filter((r: any) => r.severity === 'critical').length,
      high: topRisks.filter((r: any) => r.severity === 'high').length,
      medium: topRisks.filter((r: any) => r.severity === 'medium').length,
      low: topRisks.filter((r: any) => r.severity === 'low').length
    };

    const prompt = `Skriv en kortfattad, fokuserad modulrapport för "${aggregate.module}".

DATA:
- Transaktioner: ${aggregate.summary_json?.total_transactions?.toLocaleString('sv-SE') || 0}
- Belopp in: ${aggregate.summary_json?.total_amount_in?.toLocaleString('sv-SE') || 0} SEK
- Belopp ut: ${aggregate.summary_json?.total_amount_out?.toLocaleString('sv-SE') || 0} SEK
- Totalt fynd: ${aggregate.flag_count}
- Riskpoäng: ${aggregate.risk_score}/100
- Svårighetsgrad: ${severityDistribution.critical} kritisk, ${severityDistribution.high} hög, ${severityDistribution.medium} medium, ${severityDistribution.low} låg

TOP 3-5 FYND:
${topRisks.slice(0, 5).map((risk: any, i: number) => 
  `${i + 1}. ${risk.description} (${risk.severity}, poäng: ${risk.score})`
).join('\n')}

Skriv en rapport enligt följande struktur (totalt 300-500 ord):

**1. MODULÖVERSIKT** (2-3 meningar)
Beskriv kort vilken data som analyserades, tidsperiod och syfte.

**2. SAMMANFATTNING AV FYND**
Ange totalt antal fynd och fördelning per svårighetsgrad som punktlista.

**3. NYCKELFYND** (Top 3-5)
För varje fynd: beskrivning, belopp/datum om relevant, svårighetsgrad och potentiell konsekvens.

**4. RISKBEDÖMNING** (1 stycke)
Total riskpoäng med motivering baserad på fynden.

**5. REKOMMENDATIONER** (2-3 punkter)
Konkreta åtgärder baserade på de viktigaste fynden.

Använd professionell, koncis svenska. Håll texten fokuserad och undvik onödiga detaljer.`;

    const section = await callPremiumText([
      { role: 'system', content: 'Du är en auktoriserad revisor som skriver kortfattade modulrapporter på svenska.' },
      { role: 'user', content: prompt }
    ]);

    // Varna om token-limit nåtts
    console.log(`📝 Modulsektion '${aggregate.module}' genererad (längd: ${section.length} tecken)`);
    if (section.length > 60000) {
      console.warn(`⚠️ Modulsektion '${aggregate.module}' extremt lång (${section.length} tecken) - kan ha nått token-limit`);
    }

    return section;
  }

  private async generateRecommendations(aggregates: AggregateResult[]): Promise<string> {
    const allRisks = aggregates.flatMap(agg => 
      agg.summary_json?.all_flags || []
    ).sort((a: any, b: any) => b.score - a.score);

    const criticalRisks = allRisks.filter((risk: any) => risk.severity === 'critical').slice(0, 3);
    const highRisks = allRisks.filter((risk: any) => risk.severity === 'high').slice(0, 5);

    const prompt = `Baserat på revisionsresultaten, skriv en sektion med rekommendationer och åtgärdsförslag.

KRITISKA RISKER:
${criticalRisks.map((risk: any, i: number) => 
  `${i + 1}. ${risk.description} (Score: ${risk.score})`
).join('\n')}

HÖGA RISKER:
${highRisks.map((risk: any, i: number) => 
  `${i + 1}. ${risk.description} (Score: ${risk.score})`
).join('\n')}

TOTALA RISKSTATISTIK:
- Totalt antal fynd: ${allRisks.length}
- Kritiska fynd: ${allRisks.filter((r: any) => r.severity === 'critical').length}
- Högrisk fynd: ${allRisks.filter((r: any) => r.severity === 'high').length}

Skriv en strukturerad rekommendationssektion som inkluderar:
1. Prioriterade åtgärder för kritiska risker
2. Rekommendationer för förbättring av kontrollmiljön
3. Förslag på policy- och processförändringar
4. Tidsramar för implementering
5. Uppföljningsplan

Ordna rekommendationerna efter prioritet (kritisk, hög, medium).
Ge konkreta och genomförbara förslag.
Använd professionell svenska och revisionsrelaterad terminologi.
Sektionen ska vara ca 400-600 ord.`;

    const recommendations = await callPremiumText([
      { role: 'system', content: 'Du är en auktoriserad revisor som ger strategiska rekommendationer på svenska.' },
      { role: 'user', content: prompt }
    ]);

    // Varna om token-limit nåtts
    console.log(`📝 Rekommendationer genererade (längd: ${recommendations.length} tecken)`);
    if (recommendations.length > 60000) {
      console.warn(`⚠️ Rekommendationer extremt långa (${recommendations.length} tecken) - kan ha nått token-limit`);
    }

    return recommendations;
  }

  private assembleFinalReport(components: any): any {
    const { revisionInfo, executiveSummary, moduleSections, recommendations, aggregates } = components;

    // Beräkna sammanfattande statistik
    const totalFindings = aggregates.reduce((sum: number, agg: any) => sum + agg.flag_count, 0);
    const criticalFindings = aggregates.reduce((sum: number, agg: any) => sum + agg.high_risk_flags, 0);
    
    const reportData = {
      revision_id: revisionInfo.id,
      company_name: revisionInfo.company_name,
      org_number: revisionInfo.org_number,
      report_date: new Date().toISOString(),
      executive_summary: executiveSummary,
      module_sections: moduleSections,
      recommendations: recommendations,
      statistics: {
        total_findings: totalFindings,
        critical_findings: criticalFindings,
        modules_analyzed: aggregates.length,
        analysis_period: {
          start: revisionInfo.started_at,
          end: new Date().toISOString()
        }
      }
    };

    // Generera HTML-rapport
    const reportHtml = this.generateReportHTML(reportData);

    return {
      report_json: reportData,
      report_html: reportHtml,
      executive_summary: executiveSummary,
      total_findings: totalFindings,
      critical_findings: criticalFindings
    };
  }

  private generateReportHTML(data: any): string {
    return `
<!DOCTYPE html>
<html lang="sv">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Revisionsrapport - ${data.company_name}</title>
    <style>
        body { font-family: 'Segoe UI', Arial, sans-serif; line-height: 1.6; max-width: 800px; margin: 0 auto; padding: 20px; }
        .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 30px; }
        .company-info { background: #f5f5f5; padding: 15px; margin: 20px 0; border-radius: 5px; }
        .section { margin: 30px 0; }
        .section h2 { color: #333; border-bottom: 1px solid #ccc; padding-bottom: 10px; }
        .statistics { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px; margin: 20px 0; }
        .stat-card { background: #f9f9f9; padding: 15px; border-radius: 5px; text-align: center; }
        .stat-number { font-size: 24px; font-weight: bold; color: #2563eb; }
        .critical { color: #dc2626; }
        .footer { text-align: center; margin-top: 50px; padding-top: 20px; border-top: 1px solid #ccc; font-size: 0.9em; color: #666; }
    </style>
</head>
<body>
    <div class="header">
        <h1>Revisionsrapport</h1>
        <h2>${data.company_name}</h2>
        <p>Organisationsnummer: ${data.org_number}</p>
        <p>Rapportdatum: ${new Date(data.report_date).toLocaleDateString('sv-SE')}</p>
    </div>

    <div class="company-info">
        <h3>Uppdragsinformation</h3>
        <p><strong>Företag:</strong> ${data.company_name}</p>
        <p><strong>Organisationsnummer:</strong> ${data.org_number}</p>
        <p><strong>Analysperiod:</strong> ${new Date(data.statistics.analysis_period.start).toLocaleDateString('sv-SE')} - ${new Date(data.statistics.analysis_period.end).toLocaleDateString('sv-SE')}</p>
    </div>

    <div class="statistics">
        <div class="stat-card">
            <div class="stat-number">${data.statistics.total_findings}</div>
            <div>Totalt antal fynd</div>
        </div>
        <div class="stat-card">
            <div class="stat-number critical">${data.statistics.critical_findings}</div>
            <div>Kritiska fynd</div>
        </div>
        <div class="stat-card">
            <div class="stat-number">${data.statistics.modules_analyzed}</div>
            <div>Analyserade moduler</div>
        </div>
    </div>

    <div class="section">
        <h2>Sammanfattning</h2>
        <div>${data.executive_summary.replace(/\n/g, '</p><p>')}</div>
    </div>

    ${Object.entries(data.module_sections).map(([module, content]: [string, any]) => `
    <div class="section">
        <h2>Modul: ${module}</h2>
        <div>${content.replace(/\n/g, '</p><p>')}</div>
    </div>
    `).join('')}

    <div class="section">
        <h2>Rekommendationer och åtgärdsförslag</h2>
        <div>${data.recommendations.replace(/\n/g, '</p><p>')}</div>
    </div>

    <div class="footer">
        <p>Denna rapport har genererats automatiskt av AuditAI Full Analysis System</p>
        <p>Rapport-ID: ${data.revision_id}</p>
    </div>
</body>
</html>`;
  }

  private async saveFinalReport(revisionId: string, reportData: any, executiveSummary: string): Promise<void> {
    // Spara i revision_reports tabellen
    const { error } = await supabase
      .from('revision_reports')
      .insert({
        revision_id: revisionId,
        report_json: reportData.report_json,
        report_html: reportData.report_html,
        executive_summary: executiveSummary,
        total_findings: reportData.total_findings,
        critical_findings: reportData.critical_findings,
        generated_by_model: 'gpt-5-premium'
      });

    if (error) throw error;

    // Uppdatera revision status
    await supabase
      .from('revisions')
      .update({
        status: 'completed',
        completed_at: new Date().toISOString()
      })
      .eq('id', revisionId);
  }
}